import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/primitives/Button";
import type { ClientEnvironment } from "./client-model";
import { envOptions } from "./client-model";

/**
 * Mints the HubSpot install link for a client and environment, and copies it.
 *
 * Under Settings rather than in the header (spec §3.3): it is a one-time step
 * in onboarding, and a header slot would offer it every day of the client's
 * life for the one week it is useful.
 */
export function InstallLinkButton({
  organizationId,
  environments,
  defaultEnvironment,
}: {
  organizationId: string;
  environments: ClientEnvironment[];
  defaultEnvironment?: string | null;
}) {
  const [label, setLabel] = useState<"Copy install link" | "Copied" | "Couldn't copy">("Copy install link");
  const [busy, setBusy] = useState(false);
  const [environment, setEnvironment] = useState(defaultEnvironment ?? "");

  // Follow the header's environment selector; changing it there re-targets the link.
  useEffect(() => { setEnvironment(defaultEnvironment ?? ""); }, [defaultEnvironment]);

  /** Mint the install link for the selected environment and put it on the
   *  clipboard, saying so on the button either way. */
  async function copy() {
    if (busy) return;
    setBusy(true);
    try {
      const { url } = await api.hubspotInstallUrl(organizationId, environment || undefined);
      await navigator.clipboard.writeText(url);
      setLabel("Copied");
      setTimeout(() => setLabel("Copy install link"), 1500);
    } catch {
      setLabel("Couldn't copy");
      setTimeout(() => setLabel("Copy install link"), 2000);
    } finally {
      setBusy(false);
    }
  }

  return (
    <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
      <select
        className="field__input"
        aria-label="Install environment"
        value={environment}
        onChange={(e) => setEnvironment(e.target.value)}
      >
        {envOptions(environments, environment, "Any environment").map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <Button variant="secondary" onClick={() => void copy()} disabled={busy}>
        {label}
      </Button>
    </span>
  );
}
